import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useCanEdit } from "../hooks/useCanEdit.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { formatMoney } from "../lib/format-fr.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

type RecipientType = "intercompany" | "technician" | "partner";
type Step = 1 | 2 | 3;

interface SelectedItem {
  vehicleServiceJobId: number;
  technicianId: number;
  // Snapshot para o preview (não vai pro backend; backend calcula a partir do vsj):
  dossierCode: string;
  customerName: string;
  technicianName: string;
  plate: string | null;
  invoiceDate: string | null;
  /** Faturado total das lines do técnico no dossier (= line.faturado; pode estar igual ao orçamento por bug do importador antigo). */
  faturadoTotal: number;
  /**
   * F-wizard-preview-v3 — amounts V3 do técnico no dossier para preview correto.
   * Backend calcula gross dependendo de recipientType (tec+interm em
   * intercompany; só tec em technician; só interm em partner). Refletimos
   * a mesma lógica aqui.
   */
  technicianAmount: number;
  intermediaryAmount: number;
  /** Para lines com commission_model='legacy', fallback usa este valor como tec. */
  legacyTechCommissionAmount: number;
  /**
   * F-wizard-resumo-financeiro (2026-05-27) — Para o panorama "Resumo
   * financeiro" no Step 3. Orçamento bruto (Prix Brut) das lines do
   * técnico no dossier, e o clientPct efetivo (mesmo em todas as lines
   * de um mesmo dossier no modelo F8).
   */
  orcamentoTotal: number;
  clientPct: number;
  /**
   * F-panorama-use-stored-amounts (2026-05-27) — V3 amounts já calculados
   * pelo calculateCommission no write da line. Source of truth pra retenção
   * cliente e parte do Euro Storm, independente de base (budget/invoiced).
   * Some por dossier (linhas do técnico).
   */
  clientAmount: number;
  eurostormAmount: number;
}

export default function ProformaNew() {
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const [location] = useLocation();
  const tr = useT();

  const RECIPIENT_LABEL: Record<RecipientType, string> = {
    intercompany: tr("proformaNew.recipientTypeIntercompany"),
    technician: tr("proformaNew.recipientTypeTechnician"),
    partner: tr("proformaNew.recipientTypePartner"),
  };

  const companiesQuery = trpc.companies.list.useQuery();
  const accessibleCompanies = (companiesQuery.data ?? []).filter((c) =>
    (user?.allowedCompanyIds ?? []).includes(c.id),
  );
  const [companyId, setCompanyId] = useSelectedCompany(accessibleCompanies);
  // Promove primeira empresa quando companies.list aterrissa (useState init
  // só roda uma vez, com array vazio na primeira render).

  // F-proforma-passthrough-selection — lê dossierIds da URL (vem de /dossiers
  // bulk action "Gerar proforma"). Quando presente, pula step 2 e auto-popula
  // selectedItems com esses dossiers. wouter useLocation só dá pathname; a
  // query string vem de window.location.search.
  void location; // evita warning de unused
  const [preselectedDossierIds] = useState<number[]>(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("dossierIds");
    if (!raw) return [];
    return raw
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  });
  const hasPreselection = preselectedDossierIds.length > 0;

  const [step, setStep] = useState<Step>(1);

  // ─── Step 1 — Header ────────────────────────────────────────────────────────
  const [recipientType, setRecipientType] = useState<RecipientType>("intercompany");
  const [recipientCompanyId, setRecipientCompanyId] = useState<number | null>(null);
  const [recipientTechnicianId, setRecipientTechnicianId] = useState<number | null>(null);
  const [recipientPartnerCompanyId, setRecipientPartnerCompanyId] = useState<number | null>(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientEmailTouched, setRecipientEmailTouched] = useState(false);
  const [language, setLanguage] = useState<"fr" | "en" | "pt">("fr");
  const [notes, setNotes] = useState("");
  const [customMessage, setCustomMessage] = useState("");

  const technicianListQuery = trpc.technicians.list.useQuery(
    { companyId: companyId ?? 0, active: true, limit: 200, offset: 0 },
    // F-proforma-editable-items — também habilita no step 2 (filtro "Todos
    // técnicos" da barra), não só quando o destinatário é técnico.
    { enabled: companyId !== null && (recipientType === "technician" || step === 2) },
  );

  // Auto-preenche email do destinatário a partir de companies.email/
  // technicians.email. Para de auto-preencher se o usuário tocou o campo.
  useEffect(() => {
    if (recipientEmailTouched) return;
    if (recipientType === "intercompany" && recipientCompanyId != null) {
      const c = companiesQuery.data?.find((x) => x.id === recipientCompanyId);
      setRecipientEmail(c?.email ?? "");
    } else if (recipientType === "technician" && recipientTechnicianId != null) {
      const t = technicianListQuery.data?.rows.find((x) => x.id === recipientTechnicianId);
      setRecipientEmail(t?.email ?? "");
    } else if (recipientType === "partner") {
      // Partner externo — sem fonte automática
      setRecipientEmail("");
    } else {
      setRecipientEmail("");
    }
  }, [
    recipientType,
    recipientCompanyId,
    recipientTechnicianId,
    companiesQuery.data,
    technicianListQuery.data?.rows,
    recipientEmailTouched,
  ]);

  // ─── Step 2 — Itens ─────────────────────────────────────────────────────────
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [filterTechnicianId, setFilterTechnicianId] = useState<number | "all">("all");
  const [q, setQ] = useState("");
  // F-proforma-editable-items — filtros dedicados ao adicionar dossiers (step 2).
  const [filterCustomerId, setFilterCustomerId] = useState<number | "all">("all");
  const [filterPlate, setFilterPlate] = useState("");
  // Map vehicleServiceJobId -> SelectedItem (dedup por dossier)
  const [selectedItems, setSelectedItems] = useState<Map<number, SelectedItem>>(new Map());
  // F-proforma-editable-items — guarda de auto-popularização ÚNICA (preselection).
  // Sem isto, remover dossiers e renavegar (ou trocar a query p/ browse) re-popularia
  // o que o usuário tirou.
  const [autoPopulated, setAutoPopulated] = useState(false);

  // F-proforma-editable-items — lista de clientes p/ o dropdown de filtro (step 2).
  const customersFilterQuery = trpc.customers.list.useQuery(
    { companyId: companyId ?? 0, active: true, limit: 200, offset: 0 },
    { enabled: companyId !== null && step === 2 },
  );

  const linesQuery = trpc.serviceLines.listPagamentosTecnicos.useQuery(
    {
      companyId: companyId ?? 0,
      from: from || undefined,
      to: to || undefined,
      technicianId: filterTechnicianId === "all" ? undefined : filterTechnicianId,
      q: q || undefined,
      customerId: filterCustomerId === "all" ? undefined : filterCustomerId,
      plate: filterPlate.trim() || undefined,
      // F-proforma-passthrough-selection — quando dossierIds vieram via URL,
      // filtra a query backend só para esses dossiers e busca os 4 valores
      // V3 (technicianAmount/intermediaryAmount) para preview correto.
      // limit MAX 200 (PAGE_SIZE_MAX no backend); enviar mais retorna 400
      // BAD_REQUEST silencioso — daí "Nenhuma linha retornada" enganoso.
      // F-proforma-editable-items — restringe aos dossiers da URL só enquanto o
      // usuário NÃO está no step 2. No step 2 (browse p/ adicionar mais) a query
      // passa a ser por filtros (data/técnico/busca), não travada na preseleção.
      dossierIds: hasPreselection && step !== 2 ? preselectedDossierIds : undefined,
      limit: hasPreselection && step !== 2 ? 200 : 100,
      offset: 0,
    },
    {
      // Quando preselecionado, query roda sempre (mesmo fora do step 2) para
      // popular selectedItems automaticamente. Sem preselection, só roda no
      // step 2 onde o usuário escolhe dossiers manualmente.
      enabled: companyId !== null && (hasPreselection || step === 2),
    },
  );

  // Badge proforma ativa
  const pageLineIds = useMemo(
    () => (linesQuery.data?.rows ?? []).map((r) => r.id),
    [linesQuery.data?.rows],
  );
  const activeProformasQuery = trpc.proformaRequests.linesInActiveProforma.useQuery(
    { companyId: companyId ?? 0, lineIds: pageLineIds },
    { enabled: companyId !== null && step === 2 && pageLineIds.length > 0 },
  );
  const linesInProforma = activeProformasQuery.data ?? {};

  // Para a UI, agrupa as linhas por dossier (mesmo dossier => mesma proforma).
  // F-wizard-preview-v3 — soma os 4 campos V3 por dossier (faturado é mantido
  // para info; gross efetivo da proforma depende do recipientType e é
  // calculado em `totals`).
  // F-proforma-item-por-tecnico (CEO 2026-10-01, FJ-929-KH "deveria ser
  // 2 360"): agrupa por carro × TÉCNICO. Antes agrupava só por carro e somava
  // a metade de montagem do Bernardino (21 €) à comissão do Cruspeyre; o item
  // gravado já era por técnico (buildItemSnapshot), só a tela mostrava errado.
  const groupedByDossier = useMemo(() => {
    const groups = new Map<
      string,
      {
        dossierId: number;
        dossierCode: string;
        plate: string | null;
        invoiceDate: string | null;
        customerName: string;
        technicianId: number;
        technicianName: string;
        /** F-wizard-auto-detect-flow: para inferir o recipientType correto. */
        technicianIntermediaryCompanyId: number | null;
        /** CEO 2026-10-13 — intermediário do PAR técnico × cliente × ateliê. */
        pairIntermediaryCompanyId: number | null;
        /** % do intermediário no par × % gravado nas linhas (divergem = linha velha). */
        pairIntermediaryPct: number | null;
        lineIntermediaryPct: number | null;
        faturadoTotal: number;
        technicianAmount: number;
        intermediaryAmount: number;
        legacyTechCommissionAmount: number;
        orcamentoTotal: number;
        clientPct: number;
        /** F-panorama-use-stored-amounts: V3 amounts do DB. */
        clientAmount: number;
        eurostormAmount: number;
        anyLineId: number; // pra checar badge
        blocked: boolean;
        /** Tem linha de PDR — é o técnico "principal" do carro. */
        hasDsp: boolean;
      }
    >();
    for (const r of linesQuery.data?.rows ?? []) {
      const chave = `${r.dossierId}:${r.technicianId}`;
      const existing = groups.get(chave);
      const lineFat = Number(r.faturado ?? 0);
      const lineOrc = Number(r.orcamento ?? 0);
      const lineTecV3 = Number(r.technicianAmount ?? 0);
      const lineIntermV3 = Number(r.intermediaryAmount ?? 0);
      const lineLegacy =
        r.commissionModel === "legacy" ? Number(r.technicianCommissionAmount ?? 0) : 0;
      const lineClientPct = Number(r.clientPct ?? 0);
      const lineClientAmt = Number(r.clientAmount ?? 0);
      const lineEurostormAmt = Number(r.eurostormAmount ?? 0);
      const isBlocked = Boolean(linesInProforma[r.id]);
      if (existing) {
        existing.faturadoTotal += lineFat;
        existing.orcamentoTotal += lineOrc;
        existing.technicianAmount += lineTecV3;
        existing.intermediaryAmount += lineIntermV3;
        existing.legacyTechCommissionAmount += lineLegacy;
        existing.clientAmount += lineClientAmt;
        existing.eurostormAmount += lineEurostormAmt;
        // clientPct é o mesmo nas lines de um mesmo dossier — preserva o do primeiro
        existing.blocked = existing.blocked || isBlocked;
        existing.hasDsp = existing.hasDsp || r.serviceType === "dsp";
        // Guarda o % de uma linha que DIVERGE do par (basta uma para avisar).
        if (
          !r.isShare &&
          r.commissionModel === "complementary" &&
          existing.pairIntermediaryPct != null &&
          Math.abs(Number(r.intermediaryPct ?? 0) - existing.pairIntermediaryPct) > 0.005
        ) {
          existing.lineIntermediaryPct = Number(r.intermediaryPct ?? 0);
        }
      } else {
        groups.set(chave, {
          dossierId: r.dossierId,
          dossierCode: r.dossier.dossierCode,
          plate: r.dossier.plate,
          invoiceDate: r.dossier.invoiceDate,
          customerName: r.customer.displayName,
          technicianId: r.technicianId,
          technicianName: r.technician.name,
          technicianIntermediaryCompanyId: r.technician.intermediaryCompanyId,
          pairIntermediaryCompanyId: r.pair?.intermediaryCompanyId ?? null,
          pairIntermediaryPct: r.pair ? (r.pair.intermediaryPct ?? 0) : null,
          lineIntermediaryPct:
            !r.isShare && r.commissionModel === "complementary"
              ? Number(r.intermediaryPct ?? 0)
              : null,
          faturadoTotal: lineFat,
          orcamentoTotal: lineOrc,
          technicianAmount: lineTecV3,
          intermediaryAmount: lineIntermV3,
          legacyTechCommissionAmount: lineLegacy,
          clientPct: lineClientPct,
          clientAmount: lineClientAmt,
          eurostormAmount: lineEurostormAmt,
          anyLineId: r.id,
          blocked: isBlocked,
          hasDsp: r.serviceType === "dsp",
        });
      }
    }
    return Array.from(groups.values());
  }, [linesQuery.data?.rows, linesInProforma]);

  function toggleDossier(g: (typeof groupedByDossier)[number]) {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (next.get(g.dossierId)?.technicianId === g.technicianId) {
        next.delete(g.dossierId);
      } else {
        next.set(g.dossierId, {
          vehicleServiceJobId: g.dossierId,
          technicianId: g.technicianId,
          dossierCode: g.dossierCode,
          customerName: g.customerName,
          technicianName: g.technicianName,
          plate: g.plate,
          invoiceDate: g.invoiceDate,
          faturadoTotal: g.faturadoTotal,
          technicianAmount: g.technicianAmount,
          intermediaryAmount: g.intermediaryAmount,
          legacyTechCommissionAmount: g.legacyTechCommissionAmount,
          orcamentoTotal: g.orcamentoTotal,
          clientPct: g.clientPct,
          clientAmount: g.clientAmount,
          eurostormAmount: g.eurostormAmount,
        });
      }
      return next;
    });
  }

  // F-proforma-editable-items (2026-06-03) — remove um dossier da seleção (botão
  // × no step 3). Reativo: totals/panorama recalculam via useMemo sobre itemsArray.
  function removeDossier(dossierId: number) {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      next.delete(dossierId);
      return next;
    });
  }

  // F-proforma-passthrough-selection — quando vier com dossierIds na URL,
  // auto-popula selectedItems com os dossiers retornados pela query.
  // Roda só uma vez por mudança em groupedByDossier (idempotente via Map).
  useEffect(() => {
    if (!hasPreselection || autoPopulated || groupedByDossier.length === 0) return;
    setSelectedItems((prev) => {
      // Já populou? não refaz.
      if (prev.size > 0) return prev;
      const next = new Map(prev);
      // Um item por carro: o do técnico destinatário, se já escolhido; senão o
      // técnico do PDR (o montador só entra se for o único do carro).
      const ordenados = [...groupedByDossier].sort((a, b) => Number(b.hasDsp) - Number(a.hasDsp));
      for (const g of ordenados) {
        if (recipientTechnicianId != null && g.technicianId !== recipientTechnicianId) continue;
        if (next.has(g.dossierId)) continue;
        next.set(g.dossierId, {
          vehicleServiceJobId: g.dossierId,
          technicianId: g.technicianId,
          dossierCode: g.dossierCode,
          customerName: g.customerName,
          technicianName: g.technicianName,
          plate: g.plate,
          invoiceDate: g.invoiceDate,
          faturadoTotal: g.faturadoTotal,
          technicianAmount: g.technicianAmount,
          intermediaryAmount: g.intermediaryAmount,
          legacyTechCommissionAmount: g.legacyTechCommissionAmount,
          orcamentoTotal: g.orcamentoTotal,
          clientPct: g.clientPct,
          clientAmount: g.clientAmount,
          eurostormAmount: g.eurostormAmount,
        });
      }
      return next;
    });
    setAutoPopulated(true);
  }, [hasPreselection, autoPopulated, groupedByDossier, recipientTechnicianId]);

  // F-wizard-auto-detect-flow (2026-05-27) — Quando dossiers vêm
  // pré-selecionados, detecta o tipo de proforma a partir do técnico:
  //   - sem intermediário                   → 'technician' (direto)
  //   - intermediário in_group              → 'intercompany' (paga tec+interm
  //                                            para a empresa do grupo)
  //   - intermediário fora do grupo         → 'partner' (paga só interm)
  // Espelha `detectProformaFlow` do backend (createForTechnician).
  // Só roda UMA vez por mudança de seleção e quando o user ainda não tocou
  // (recipientCompanyId/recipientTechnicianId/recipientPartnerCompanyId
  // ainda no default null). Assume: todos os dossiers selecionados têm o
  // mesmo técnico OU técnicos com o mesmo intermediário; em casos mistos
  // mantém o default 'intercompany' e o user resolve manualmente.
  const [autoDetectApplied, setAutoDetectApplied] = useState(false);
  useEffect(() => {
    if (!hasPreselection || autoDetectApplied) return;
    if (groupedByDossier.length === 0 || (companiesQuery.data?.length ?? 0) === 0) return;
    if (
      recipientCompanyId != null ||
      recipientTechnicianId != null ||
      recipientPartnerCompanyId != null
    ) {
      setAutoDetectApplied(true);
      return; // user já tocou
    }
    // CEO 2026-10-13: o destinatário é o técnico do PDR (o montador só se for o
    // único do carro) — mesma regra do auto-popular dos itens.
    const firstTech = groupedByDossier.find((g) => g.hasDsp) ?? groupedByDossier[0];
    if (!firstTech) return;
    // CEO 2026-10-13 (HB-244-TH): o intermediário vive no PAR (F-tcc2); o do
    // cadastro do técnico é legado e vence só se existir.
    const intermId =
      firstTech.technicianIntermediaryCompanyId ?? firstTech.pairIntermediaryCompanyId;
    if (intermId == null) {
      // Técnico direto (sem intermediário) → recipient é o próprio técnico
      setRecipientType("technician");
      setRecipientTechnicianId(firstTech.technicianId);
    } else {
      const intermCo = companiesQuery.data?.find((c) => c.id === intermId);
      const isGroup = Boolean(intermCo?.isGroupMember);
      if (isGroup) {
        setRecipientType("intercompany");
        setRecipientCompanyId(intermId);
      } else {
        setRecipientType("partner");
        setRecipientPartnerCompanyId(intermId);
      }
    }
    setAutoDetectApplied(true);
  }, [
    hasPreselection,
    autoDetectApplied,
    groupedByDossier,
    companiesQuery.data,
    recipientCompanyId,
    recipientTechnicianId,
    recipientPartnerCompanyId,
  ]);

  // CEO 2026-10-13 (HB-244-TH): par corrigido DEPOIS do cálculo das linhas →
  // a proforma sairia com a fatia antiga. Lista os carros para corrigir antes.
  const linhasForaDoPar = useMemo(
    () =>
      groupedByDossier.filter(
        (g) =>
          g.pairIntermediaryPct != null &&
          g.lineIntermediaryPct != null &&
          Math.abs(g.pairIntermediaryPct - g.lineIntermediaryPct) > 0.005,
      ),
    [groupedByDossier],
  );
  const pctTxt = (n: number) => `${n.toFixed(2).replace(/\.?0+$/, "")} %`;
  const avisoForaDoPar =
    linhasForaDoPar.length > 0 ? (
      <div
        className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 space-y-1.5"
        data-testid="proforma-pair-mismatch"
      >
        <p className="font-semibold">⚠ {tr("proformaNew.pairMismatchTitle")}</p>
        <ul className="space-y-0.5">
          {linhasForaDoPar.map((g) => (
            <li key={`${g.dossierId}:${g.technicianId}`}>
              <span className="whitespace-nowrap font-mono">{g.plate ?? g.dossierCode}</span> ·{" "}
              {g.technicianName}: {tr("proformaNew.pairMismatchPair")}{" "}
              <b>{pctTxt(g.pairIntermediaryPct ?? 0)}</b> · {tr("proformaNew.pairMismatchLines")}{" "}
              <b>{pctTxt(g.lineIntermediaryPct ?? 0)}</b> ·{" "}
              <a
                href={`/dossiers/${g.dossierId}?tab=linhas`}
                className="whitespace-nowrap text-lime hover:underline"
              >
                {tr("proformaNew.pairMismatchOpen")} »
              </a>
            </li>
          ))}
        </ul>
        <p className="text-amber-200/80">{tr("proformaNew.pairMismatchHint")}</p>
      </div>
    ) : null;

  // ─── Step 3 — Preview & Submit ─────────────────────────────────────────────
  const [retentionPercent, setRetentionPercent] = useState("0");
  const [error, setError] = useState<string | null>(null);

  const createMutation = trpc.proformaRequests.createWithItems.useMutation({
    // F-bs-mirror-navfix (2026-05-31) — navegação REAL (full reload), não SPA.
    // Antes era `setLocation(...)` (wouter, client-side). O problema: uma aba
    // aberta há tempo navegava client-side para o detalhe e renderizava o
    // módulo ProformaDetail ANTIGO em memória — sem o botão "Importar pra
    // Brazilian Storms", por ex. Como isto roda logo após criar a proforma,
    // recarregar a página inteira não tem custo de UX e garante que o detalhe
    // (e todo o bundle) venha na versão atual. Cura o ciclo de aba velha.
    onSuccess: (data) => {
      window.location.assign(`/proformas/${data.id}`);
    },
    onError: (e) => setError(e.message),
  });

  const itemsArray = Array.from(selectedItems.values());

  // F-proforma-editable-items — pré-voo do R12: lista TODOS os dossiers
  // selecionados que já estão em outra proforma ativa (o create só falha no
  // primeiro). Alimenta o banner do step 3 + botão "remover todos".
  // F-peca-paga-uma-vez — o conflito é do TÉCNICO no carro: o carro pode
  // estar na proforma de outro técnico (PDR de um, dégarnissage de outro).
  const selectedPairs = useMemo(
    () =>
      Array.from(selectedItems.values()).map((it) => ({
        dossierId: it.vehicleServiceJobId,
        technicianId: it.technicianId,
      })),
    [selectedItems],
  );
  const conflictsQuery = trpc.proformaRequests.dossiersInActiveProforma.useQuery(
    { companyId: companyId ?? 0, items: selectedPairs },
    { enabled: companyId !== null && step === 3 && selectedPairs.length > 0 },
  );
  const conflicts = conflictsQuery.data ?? [];
  function removeAllConflicts() {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      for (const c of conflicts) next.delete(c.dossierId);
      return next;
    });
  }

  // F-wizard-preview-v3 — espelha buildItemSnapshot do backend:
  //   intercompany → tec V3 + legacy_tec + interm V3
  //   technician   → tec V3 + legacy_tec
  //   partner      → só interm V3
  // legacy_tec preenche o lado do técnico quando a line é legacy (technicianAmount NULL).
  const totals = useMemo(() => {
    const ret = parseFloat(retentionPercent.replace(",", ".")) || 0;
    let gross = 0;
    for (const it of itemsArray) {
      switch (recipientType) {
        case "intercompany":
          gross += it.technicianAmount + it.legacyTechCommissionAmount + it.intermediaryAmount;
          break;
        case "technician":
          gross += it.technicianAmount + it.legacyTechCommissionAmount;
          break;
        case "partner":
          gross += it.intermediaryAmount;
          break;
      }
    }
    const retentionAmount = (gross * ret) / 100;
    return { gross, retentionAmount, net: gross - retentionAmount, ret };
  }, [itemsArray, retentionPercent, recipientType]);

  // Para mostrar o gross de UM item específico no preview/lista. Usa a mesma
  // semântica de `totals.gross` mas para 1 dossier.
  function itemGrossForPreview(it: SelectedItem): number {
    switch (recipientType) {
      case "intercompany":
        return it.technicianAmount + it.legacyTechCommissionAmount + it.intermediaryAmount;
      case "technician":
        return it.technicianAmount + it.legacyTechCommissionAmount;
      case "partner":
        return it.intermediaryAmount;
    }
  }

  function recipientLabelForPreview(): string {
    if (recipientType === "intercompany") {
      return accessibleCompanies.find((c) => c.id === recipientCompanyId)?.name ?? "?";
    }
    if (recipientType === "technician") {
      return (
        technicianListQuery.data?.rows.find((t) => t.id === recipientTechnicianId)?.name ??
        groupedByDossier.find((g) => g.technicianId === recipientTechnicianId)?.technicianName ??
        "?"
      );
    }
    return (
      companiesQuery.data?.find((c) => c.id === recipientPartnerCompanyId)?.name ??
      "(partner externo)"
    );
  }

  function step1Valid(): boolean {
    if (companyId === null) return false;
    if (recipientType === "intercompany")
      return recipientCompanyId !== null && recipientCompanyId !== companyId;
    if (recipientType === "technician") return recipientTechnicianId !== null;
    return recipientPartnerCompanyId !== null;
  }

  function step2Valid(): boolean {
    return selectedItems.size > 0;
  }

  function step3Valid(): boolean {
    const ret = parseFloat(retentionPercent.replace(",", "."));
    return Number.isFinite(ret) && ret >= 0 && ret <= 100;
  }

  function submit() {
    setError(null);
    const ret = parseFloat(retentionPercent.replace(",", "."));
    if (!Number.isFinite(ret) || ret < 0 || ret > 100) {
      setError(tr("proformaNew.invalidRetention"));
      return;
    }
    if (companyId === null) return;
    createMutation.mutate({
      companyId,
      issuerCompanyId: companyId,
      recipientType,
      recipientCompanyId: recipientType === "intercompany" ? recipientCompanyId! : undefined,
      recipientTechnicianId: recipientType === "technician" ? recipientTechnicianId! : undefined,
      recipientPartnerId: recipientType === "partner" ? recipientPartnerCompanyId! : undefined,
      recipientEmail: recipientEmail.trim() || undefined,
      language,
      customMessage: customMessage.trim() || undefined,
      notes: notes.trim() || undefined,
      items: itemsArray.map((it) => ({
        vehicleServiceJobId: it.vehicleServiceJobId,
        technicianId: it.technicianId,
        retentionPercent: ret,
      })),
    });
  }

  if (!user) return null;
  if (accessibleCompanies.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-neutral-400">{tr("proformaNew.noCompanyAccess")}</p>
      </Shell>
    );
  }
  // F-roles — visualizador (somente leitura) não pode criar proforma. O wizard
  // inteiro fica bloqueado; backend também rejeitaria a mutation.
  if (!canEdit) {
    return (
      <Shell>
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6 space-y-3">
          <p className="text-sm text-neutral-300">{tr("proformaNew.readOnlyNotice")}</p>
          <Link href="/proformas" className="text-sm text-lime hover:text-lime-bright">
            {tr("proformaNew.backToList")}
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-sm text-neutral-400">{tr("proformaNew.issuerCompanyLabel")}</label>
        <select
          value={companyId ?? ""}
          onChange={(e) => setCompanyId(Number(e.target.value))}
          disabled={step > 1}
          className={inputClass + " w-56"}
          title={step > 1 ? tr("proformaNew.changeCompanyTitle") : undefined}
        >
          {accessibleCompanies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <Link href="/proformas" className="ml-auto text-sm text-neutral-400 hover:text-lime">
          {tr("proformaNew.backToList")}
        </Link>
      </div>

      {/* Stepper — quando dossiers vieram pré-selecionados de /dossiers, oculta
          o step 2 (já está populado, não tem motivo para reabrir o seletor). */}
      <div className="flex items-center gap-2">
        {([1, 2, 3] as const).map((n) => (
          <button
            key={n}
            onClick={() => {
              if (n === 1) setStep(1);
              else if (n === 2 && step1Valid()) setStep(2);
              else if (n === 3 && step1Valid() && step2Valid()) setStep(3);
            }}
            disabled={(n === 2 && !step1Valid()) || (n === 3 && (!step1Valid() || !step2Valid()))}
            className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm ${
              step === n
                ? "bg-lime/15 text-lime border border-lime/40"
                : "border border-neutral-800 text-neutral-400 hover:border-neutral-700 disabled:opacity-40"
            }`}
          >
            <span className="font-mono text-xs">{n}</span>
            {n === 1
              ? tr("proformaNew.stepDestinatario")
              : n === 2
                ? tr("proformaNew.stepDossiers")
                : tr("proformaNew.stepRevisao")}
          </button>
        ))}
        {hasPreselection && (
          <span className="text-xs text-neutral-500 ml-2">
            ({preselectedDossierIds.length}
            {tr("proformaNew.preselectedFromDossiersSuffix")})
          </span>
        )}
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <section className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-4">
          <h2 className="text-base font-semibold">1 · {tr("proformaNew.step1Title")}</h2>

          <div className="flex gap-2 text-sm">
            {(["intercompany", "technician", "partner"] as const).map((t) => (
              <button
                key={t}
                onClick={() => setRecipientType(t)}
                className={`rounded-md border px-3 py-1.5 ${
                  recipientType === t
                    ? "border-lime text-lime bg-lime/10"
                    : "border-neutral-800 text-neutral-400 hover:border-neutral-700"
                }`}
              >
                {RECIPIENT_LABEL[t]}
              </button>
            ))}
          </div>

          {recipientType === "intercompany" && (
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {tr("proformaNew.recipientCompanyLabel")}
              </label>
              <select
                value={recipientCompanyId ?? ""}
                onChange={(e) => setRecipientCompanyId(Number(e.target.value) || null)}
                className={inputClass + " w-full max-w-md"}
              >
                <option value="">{tr("proformaNew.selectPlaceholder")}</option>
                {accessibleCompanies
                  .filter((c) => c.id !== companyId)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {recipientType === "technician" && (
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {tr("proformaNew.recipientTechnicianLabel")}
              </label>
              <select
                value={recipientTechnicianId ?? ""}
                onChange={(e) => setRecipientTechnicianId(Number(e.target.value) || null)}
                className={inputClass + " w-full max-w-md"}
              >
                <option value="">{tr("proformaNew.selectPlaceholder")}</option>
                {(technicianListQuery.data?.rows ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
                {/* Técnico do carro pré-selecionado fora da lista de ativos:
                    mostra mesmo assim (senão o campo aparece vazio). */}
                {recipientTechnicianId != null &&
                  technicianListQuery.data != null &&
                  !technicianListQuery.data.rows.some((t) => t.id === recipientTechnicianId) && (
                    <option value={recipientTechnicianId}>
                      {groupedByDossier.find((g) => g.technicianId === recipientTechnicianId)
                        ?.technicianName ?? `#${recipientTechnicianId}`}
                    </option>
                  )}
              </select>
            </div>
          )}

          {recipientType === "partner" && (
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {tr("proformaNew.recipientPartnerLabel")}
              </label>
              <select
                value={recipientPartnerCompanyId ?? ""}
                onChange={(e) => setRecipientPartnerCompanyId(Number(e.target.value) || null)}
                className={inputClass + " w-full max-w-md"}
              >
                <option value="">{tr("proformaNew.selectPlaceholder")}</option>
                {(companiesQuery.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-neutral-500 mt-1">{tr("proformaNew.partnerHelper")}</p>
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {tr("proformaNew.recipientEmailLabel")}
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => {
                setRecipientEmail(e.target.value);
                setRecipientEmailTouched(true);
              }}
              maxLength={320}
              className={inputClass + " w-full max-w-md"}
              placeholder={
                recipientType === "intercompany"
                  ? tr("proformaNew.emailPlaceholderIntercompany")
                  : recipientType === "technician"
                    ? tr("proformaNew.emailPlaceholderTechnician")
                    : tr("proformaNew.emailPlaceholderPartner")
              }
            />
            <p className="text-[11px] text-neutral-500 mt-1">{tr("proformaNew.emailHelper")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {tr("proformaNew.pdfLanguageLabel")}
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value as "fr" | "en" | "pt")}
                className={inputClass + " w-full"}
              >
                <option value="fr">Français</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {tr("proformaNew.notesLabel")}
              </label>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={2000}
                className={inputClass + " w-full"}
                placeholder={tr("proformaNew.notesPlaceholder")}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
              {tr("proformaNew.customMessageLabel")}
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              maxLength={2000}
              rows={2}
              className={inputClass + " w-full resize-y"}
              placeholder={tr("proformaNew.customMessagePlaceholder")}
            />
          </div>

          {/* F-preselection-status — diagnóstico visível para o usuário quando
              a auto-popularização via URL não conseguiu carregar os dossiers
              (lines sem técnico/cliente, dossiers em outra empresa, etc). */}
          {hasPreselection && (
            <div className="rounded-md border border-neutral-800 bg-neutral-900/50 p-3 text-sm">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs uppercase tracking-wider text-neutral-500">
                  {tr("proformaNew.preselectedLabel")} ({preselectedDossierIds.length})
                </span>
              </div>
              {linesQuery.isLoading && (
                <p className="text-xs text-neutral-400">{tr("proformaNew.loadingDossierLines")}</p>
              )}
              {linesQuery.error && (
                <div className="text-xs text-red-300 space-y-1">
                  <p>
                    ⚠ {tr("proformaNew.backendQueryError")} (HTTP{" "}
                    {linesQuery.error.data?.httpStatus ?? "?"}):
                  </p>
                  <p className="font-mono">{linesQuery.error.message}</p>
                </div>
              )}
              {!linesQuery.isLoading && selectedItems.size > 0 && (
                <p className="text-xs text-lime">
                  ✓ {selectedItems.size}
                  {tr("proformaNew.loadedReadySuffix")}
                </p>
              )}
              {avisoForaDoPar && <div className="mt-2">{avisoForaDoPar}</div>}
              {!linesQuery.isLoading &&
                selectedItems.size === 0 &&
                groupedByDossier.length === 0 && (
                  <div className="text-xs text-amber-300 space-y-1">
                    <p>
                      ⚠ {tr("proformaNew.noLinesReturned")} ({preselectedDossierIds.join(", ")}).
                    </p>
                    <p className="text-neutral-400">
                      {tr("proformaNew.causesPre")}{" "}
                      <a href="/dossiers" className="text-lime hover:underline">
                        /dossiers
                      </a>{" "}
                      {tr("proformaNew.causesPost")}
                    </p>
                  </div>
                )}
              {!linesQuery.isLoading && selectedItems.size === 0 && groupedByDossier.length > 0 && (
                <p className="text-xs text-neutral-400">
                  {tr("proformaNew.populatingSelection")} (groupedByDossier.length=
                  {groupedByDossier.length})
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={() => {
                // F-proforma-passthrough-selection — quando dossiers vieram
                // pré-selecionados de /dossiers, pula step 2 direto para o 3.
                setStep(hasPreselection ? 3 : 2);
              }}
              disabled={!step1Valid() || (hasPreselection && !step2Valid())}
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
              title={
                hasPreselection && !step2Valid()
                  ? tr("proformaNew.preselectionWaitTitle")
                  : undefined
              }
            >
              {hasPreselection ? tr("proformaNew.reviewArrow") : tr("proformaNew.nextArrow")}
            </button>
          </div>
        </section>
      )}

      {/* Step 2 */}
      {step === 2 && (
        <section className="space-y-3">
          <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-3">
            <h2 className="text-base font-semibold">2 · {tr("proformaNew.step2Title")}</h2>
            <div className="flex gap-3 flex-wrap">
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className={inputClass + " w-40"}
                title={tr("proformaNew.dateFromTitle")}
              />
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className={inputClass + " w-40"}
                title={tr("proformaNew.dateToTitle")}
              />
              <select
                value={filterTechnicianId === "all" ? "all" : String(filterTechnicianId)}
                onChange={(e) =>
                  setFilterTechnicianId(e.target.value === "all" ? "all" : Number(e.target.value))
                }
                className={inputClass + " w-56"}
              >
                <option value="all">{tr("proformaNew.allTechnicians")}</option>
                {(technicianListQuery.data?.rows ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tr("proformaNew.searchPlaceholder")}
                className={inputClass + " w-64"}
              />
              <select
                value={filterCustomerId === "all" ? "all" : String(filterCustomerId)}
                onChange={(e) =>
                  setFilterCustomerId(e.target.value === "all" ? "all" : Number(e.target.value))
                }
                className={inputClass + " w-56"}
                title={tr("proformaNew.filterCustomerTitle")}
              >
                <option value="all">{tr("proformaNew.allCustomers")}</option>
                {(customersFilterQuery.data?.rows ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </select>
              <input
                value={filterPlate}
                onChange={(e) => setFilterPlate(e.target.value)}
                placeholder={tr("proformaNew.platePlaceholder")}
                className={inputClass + " w-32"}
                title={tr("proformaNew.filterPlateTitle")}
              />
            </div>
          </div>

          <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
            {linesQuery.isLoading && (
              <p className="p-4 text-sm text-neutral-500">{tr("proformaNew.loading")}</p>
            )}
            {linesQuery.error && (
              <p className="p-4 text-sm text-red-400">{linesQuery.error.message}</p>
            )}
            {linesQuery.data && groupedByDossier.length === 0 && (
              <p className="p-4 text-sm text-neutral-500">{tr("proformaNew.noDossierInFilter")}</p>
            )}
            {groupedByDossier.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                  <tr>
                    <th className="w-10 px-3 py-2" />
                    <th className="text-left px-3 py-2 w-32">{tr("proformaNew.colDossier")}</th>
                    <th className="text-left px-3 py-2 w-28">{tr("proformaNew.colDate")}</th>
                    <th className="text-left px-3 py-2 w-28">{tr("proformaNew.colPlate")}</th>
                    <th className="text-left px-3 py-2">{tr("proformaNew.colCustomer")}</th>
                    <th className="text-left px-3 py-2">{tr("proformaNew.colTechnician")}</th>
                    <th className="text-right px-3 py-2 w-32 font-mono">
                      {tr("proformaNew.colGross")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-800">
                  {groupedByDossier.map((g) => {
                    const selected =
                      selectedItems.get(g.dossierId)?.technicianId === g.technicianId;
                    return (
                      <tr
                        key={`${g.dossierId}:${g.technicianId}`}
                        className={`hover:bg-neutral-800/30 ${g.blocked ? "opacity-60" : ""} ${
                          selected ? "bg-lime/5" : ""
                        }`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={g.blocked}
                            onChange={() => toggleDossier(g)}
                            title={
                              g.blocked
                                ? `${tr("proformaNew.dossierInActiveProformaPre")}${g.anyLineId})`
                                : undefined
                            }
                          />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-lime">{g.dossierCode}</span>
                            {g.blocked && (
                              <span className="text-[10px] text-amber-300">
                                {"🔒 "}
                                {tr("proformaNew.inProformaBadge")}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-neutral-400">
                          {g.invoiceDate ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-neutral-300">
                          {g.plate ?? "—"}
                        </td>
                        <td className="px-3 py-2 truncate max-w-[200px]">{g.customerName}</td>
                        <td className="px-3 py-2 truncate max-w-[160px]">{g.technicianName}</td>
                        <td
                          className="px-3 py-2 text-right font-mono"
                          title={tr("proformaNew.grossColTitle")}
                        >
                          {formatMoney(
                            recipientType === "intercompany"
                              ? g.technicianAmount +
                                  g.legacyTechCommissionAmount +
                                  g.intermediaryAmount
                              : recipientType === "technician"
                                ? g.technicianAmount + g.legacyTechCommissionAmount
                                : g.intermediaryAmount,
                            { decimals: 2 },
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-neutral-500">
              {selectedItems.size} {tr("proformaNew.dossiersSelectedSuffix")}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setStep(1)}
                className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
              >
                {tr("proformaNew.prevArrow")}
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!step2Valid()}
                className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-4 py-1.5 text-sm font-medium"
              >
                {tr("proformaNew.nextArrow")}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Step 3 */}
      {step === 3 && (
        <section className="space-y-4">
          <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-5 space-y-3">
            <h2 className="text-base font-semibold">3 · {tr("proformaNew.step3Title")}</h2>
            {avisoForaDoPar}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label={tr("proformaNew.fieldTipo")} value={RECIPIENT_LABEL[recipientType]} />
              <Field
                label={tr("proformaNew.fieldDestinatario")}
                value={recipientLabelForPreview()}
              />
              <Field label={tr("proformaNew.fieldIdioma")} value={language.toUpperCase()} />
              <Field
                label={tr("proformaNew.fieldItens")}
                value={`${itemsArray.length} ${tr("proformaNew.dossiersWordSuffix")}`}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-1">
                {tr("proformaNew.retentionLabel2")}
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={retentionPercent}
                onChange={(e) => setRetentionPercent(e.target.value)}
                className={inputClass + " w-32 font-mono"}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Field
                label={tr("proformaNew.grossTotal")}
                value={formatMoney(totals.gross, { decimals: 2 })}
                mono
              />
              <Field
                label={`${tr("proformaNew.retentionLabel")} (${totals.ret.toFixed(2)}%)`}
                value={formatMoney(totals.retentionAmount, { decimals: 2 })}
                mono
              />
              <Field
                label={tr("proformaNew.net")}
                value={formatMoney(totals.net, { decimals: 2 })}
                mono
              />
            </div>
          </div>

          {conflicts.length > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-amber-200">
                  ⚠ {conflicts.length}
                  {tr("proformaNew.conflictsBannerSuffix")}
                </p>
                <button
                  type="button"
                  onClick={removeAllConflicts}
                  className="shrink-0 rounded-md border border-amber-400/50 text-amber-200 hover:bg-amber-500/20 px-3 py-1.5 text-sm font-medium"
                >
                  {tr("proformaNew.removeConflictsPre")} {conflicts.length}{" "}
                  {tr("proformaNew.removeConflictsSuf")}
                </button>
              </div>
              <ul className="grid grid-cols-1 gap-x-4 gap-y-0.5 text-xs text-amber-100/90 sm:grid-cols-2 lg:grid-cols-3">
                {conflicts.map((c) => (
                  <li key={c.dossierId} className="font-mono">
                    {c.dossierCode} → {c.reference} ({c.status})
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-neutral-500">
              {tr("proformaNew.removeHintPre")}{" "}
              <span className="font-mono text-neutral-300">×</span>{" "}
              {tr("proformaNew.removeHintPost")}
            </p>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="rounded-md border border-lime/40 text-lime hover:bg-lime/10 px-3 py-1.5 text-sm font-medium"
            >
              + {tr("proformaNew.addEditDossiers")}
            </button>
          </div>

          <div className="rounded-lg border border-neutral-800 bg-neutral-900 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-950/50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="text-left px-3 py-2 w-32">{tr("proformaNew.colDossier")}</th>
                  <th className="text-left px-3 py-2 w-28">{tr("proformaNew.colDate")}</th>
                  <th className="text-left px-3 py-2 w-28">{tr("proformaNew.colPlate")}</th>
                  <th className="text-left px-3 py-2">{tr("proformaNew.colCustomer")}</th>
                  <th className="text-left px-3 py-2">{tr("proformaNew.colTechnician")}</th>
                  {/* F-wizard-breakdown-cols (2026-05-27): em intercompany
                      mostramos as duas partes da proforma separadas (Téc +
                      Interm) e o Total que sai pra empresa do grupo.
                      Em technician/partner há só uma parte então mostramos
                      uma única coluna com o label apropriado. */}
                  {recipientType === "intercompany" ? (
                    <>
                      <th className="text-right px-3 py-2 w-28 font-mono">
                        {tr("proformaNew.colTec")}
                      </th>
                      <th className="text-right px-3 py-2 w-28 font-mono">
                        {tr("proformaNew.colInterm")}
                      </th>
                      <th className="text-right px-3 py-2 w-28 font-mono">
                        {tr("proformaNew.colTotal")}
                      </th>
                    </>
                  ) : (
                    <th className="text-right px-3 py-2 w-32 font-mono">
                      {recipientType === "technician"
                        ? tr("proformaNew.commissionTecShort")
                        : tr("proformaNew.commissionIntermShort")}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {itemsArray.map((it) => {
                  const tecVal = it.technicianAmount + it.legacyTechCommissionAmount;
                  const intermVal = it.intermediaryAmount;
                  const totalVal = itemGrossForPreview(it);
                  return (
                    <tr key={it.vehicleServiceJobId}>
                      <td className="px-3 py-2 font-mono text-xs">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => removeDossier(it.vehicleServiceJobId)}
                            className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-neutral-700 text-neutral-500 hover:border-red-500/60 hover:text-red-400"
                            title={tr("proformaNew.removeDossierTitle")}
                            aria-label={`${tr("proformaNew.removeAria")} ${it.dossierCode}`}
                          >
                            ×
                          </button>
                          <span className="text-lime">{it.dossierCode}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-400">
                        {it.invoiceDate ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-neutral-300">
                        {it.plate ?? "—"}
                      </td>
                      <td className="px-3 py-2 truncate max-w-[200px]">{it.customerName}</td>
                      <td className="px-3 py-2 truncate max-w-[160px]">{it.technicianName}</td>
                      {recipientType === "intercompany" ? (
                        <>
                          <td
                            className="px-3 py-2 text-right font-mono text-neutral-300"
                            title={tr("proformaNew.commissionTechnicianReceive")}
                          >
                            {formatMoney(tecVal, { decimals: 2 })}
                          </td>
                          <td
                            className="px-3 py-2 text-right font-mono text-neutral-300"
                            title={tr("proformaNew.commissionIntermediary")}
                          >
                            {formatMoney(intermVal, { decimals: 2 })}
                          </td>
                          <td
                            className="px-3 py-2 text-right font-mono font-semibold text-lime"
                            title={tr("proformaNew.proformaTotalForDossier")}
                          >
                            {formatMoney(totalVal, { decimals: 2 })}
                          </td>
                        </>
                      ) : (
                        <td
                          className="px-3 py-2 text-right font-mono"
                          title={
                            recipientType === "technician"
                              ? tr("proformaNew.commissionTechnician")
                              : tr("proformaNew.commissionIntermediary")
                          }
                        >
                          {formatMoney(totalVal, { decimals: 2 })}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {/* Linha de totais — conferência visual da soma das colunas. */}
              <tfoot className="border-t-2 border-neutral-700 bg-neutral-950/60 font-semibold text-neutral-200">
                {(() => {
                  const sumTec = itemsArray.reduce(
                    (a, it) => a + it.technicianAmount + it.legacyTechCommissionAmount,
                    0,
                  );
                  const sumInterm = itemsArray.reduce((a, it) => a + it.intermediaryAmount, 0);
                  const sumTotal = itemsArray.reduce((a, it) => a + itemGrossForPreview(it), 0);
                  return (
                    <tr>
                      <td
                        className="px-3 py-2 text-xs uppercase tracking-wider text-neutral-400"
                        colSpan={5}
                      >
                        {tr("proformaNew.colTotal")} — {itemsArray.length}{" "}
                        {tr("proformaNew.dossiersWordSuffix")}
                      </td>
                      {recipientType === "intercompany" ? (
                        <>
                          <td className="px-3 py-2 text-right font-mono">
                            {formatMoney(sumTec, { decimals: 2 })}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {formatMoney(sumInterm, { decimals: 2 })}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-lime">
                            {formatMoney(sumTotal, { decimals: 2 })}
                          </td>
                        </>
                      ) : (
                        <td className="px-3 py-2 text-right font-mono text-lime">
                          {formatMoney(sumTotal, { decimals: 2 })}
                        </td>
                      )}
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>

          {/* F-wizard-resumo-financeiro (2026-05-27) — Panorama informativo
              antes de criar a proforma. NÃO altera os valores da proforma
              (que continua sendo só `totals.gross`/`net` acima). Mostra o
              fluxo completo do dinheiro nestes dossiers:
                1. Total faturado ao cliente = Σ (orcamento − clientAmount)
                   (= Total Facture da liste grêle xlsx)
                2. A pagar ao destinatário = Σ items (gross da proforma)
                3. Euro Storm fica = total faturado − a pagar
              Os legacy fields são fallbacks; a soma fecha para complementary
              e degrada elegantemente para legacy. */}
          <FinancialPanorama
            itemsArray={itemsArray}
            recipientType={recipientType}
            recipientLabel={recipientLabelForPreview()}
            proformaGross={totals.gross}
          />

          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200 whitespace-pre-wrap">
              {error}
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              onClick={() => setStep(hasPreselection ? 1 : 2)}
              disabled={createMutation.isPending}
              className="rounded-md border border-neutral-800 hover:border-neutral-700 px-4 py-1.5 text-sm"
            >
              {tr("proformaNew.prevArrow")}
            </button>
            <button
              onClick={submit}
              disabled={
                createMutation.isPending ||
                !step3Valid() ||
                selectedItems.size === 0 ||
                conflicts.length > 0
              }
              className="rounded-md bg-lime hover:bg-lime-bright text-on-lime disabled:opacity-40 px-5 py-2 text-sm font-semibold"
              title={
                selectedItems.size === 0
                  ? tr("proformaNew.addAtLeastOne")
                  : conflicts.length > 0
                    ? `${tr("proformaNew.removeConflictsTitlePre")} ${conflicts.length} ${tr("proformaNew.removeConflictsTitleSuf")}`
                    : undefined
              }
            >
              {createMutation.isPending
                ? tr("proformaNew.creating")
                : `${tr("proformaNew.createProformaDraft")}${selectedItems.size > 0 ? ` · ${selectedItems.size}` : ""}`}
            </button>
          </div>
        </section>
      )}
    </Shell>
  );
}

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none disabled:opacity-50";

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-md bg-neutral-950/40 p-2.5">
      <p className="text-[10px] uppercase tracking-wider text-neutral-500 mb-0.5">{label}</p>
      <p className={`text-sm font-semibold ${mono ? "tabular-nums font-mono" : ""}`}>{value}</p>
    </div>
  );
}

/**
 * F-wizard-resumo-financeiro + F-panorama-use-stored-amounts (2026-05-27)
 * Bloco panorama financeiro dos dossiers selecionados, mostrado no Step 3
 * ANTES de criar a proforma.
 *
 * Source of truth: o backend `listPagamentosTecnicos` retorna client_amount
 * e eurostorm_amount já calculados pelo calculateCommission no write da
 * line. Isso funciona em todos os modelos (budget, invoiced, legacy):
 *   - Budget   (4 partes): client_amount = orcamento × clientPct/100
 *   - Invoiced (3 partes): client_amount = orcamento − faturado (retenção)
 *   - Legacy   : client_amount = 0 (não rastreado)
 *
 * Cálculos (todos por dossier, depois somados):
 *   - clientAmount    = it.clientAmount (direto do DB)
 *   - faturadoCliente = orcamentoTotal − clientAmount
 *   - aPagarRecipient = proformaGross
 *   - eurostorm       = soma it.eurostormAmount (direto do DB)
 *
 * Notas:
 *   - Dossiers ainda não recalculados (faturado=orcamento + clientPct=0
 *     em invoiced) vão mostrar clientAmount=0 — porque o banco realmente
 *     tem isso. Rodar "⚡ Recalcular comissões" corrige.
 *   - "Euro Storm fica" lido do DB para consistência com /dossiers col 12.
 *   - Bloco é PURAMENTE INFORMATIVO — não muda a proforma criada.
 */
function FinancialPanorama({
  itemsArray,
  recipientType,
  recipientLabel,
  proformaGross,
}: {
  itemsArray: SelectedItem[];
  recipientType: RecipientType;
  recipientLabel: string;
  proformaGross: number;
}) {
  const tr = useT();
  // Totais agregados sobre todos os dossiers selecionados — V3 amounts
  // direto do DB, sem heurística orcamento×pct.
  let totalClientAmount = 0;
  let totalOrcamento = 0;
  let totalEurostormFromDB = 0;
  for (const it of itemsArray) {
    totalClientAmount += it.clientAmount;
    totalOrcamento += it.orcamentoTotal;
    totalEurostormFromDB += it.eurostormAmount;
  }
  const totalFaturadoCliente = totalOrcamento - totalClientAmount;
  // "Euro Storm fica" tem 2 formas equivalentes (deveriam bater): soma
  // V3 do banco vs. resíduo (faturado − proforma). Usamos a soma V3 que
  // é a source of truth do calculateCommission. Em casos onde elas
  // divergem (proforma incluir só parte do split — ex: technician direct),
  // o resíduo seria diferente. Aqui priorizamos o que está no DB.
  const eurostormKeeps = totalEurostormFromDB;
  const eurostormNegative = eurostormKeeps < -0.01;

  const recipientShortLabel =
    recipientType === "intercompany"
      ? recipientLabel
      : recipientType === "technician"
        ? `${recipientLabel} ${tr("proformaNew.technicianParenSuffix")}`
        : `${recipientLabel} ${tr("proformaNew.partnerParenSuffix")}`;

  return (
    <div className="rounded-lg border border-lime/20 bg-lime/5 p-4 space-y-2">
      <h3 className="text-sm font-semibold text-lime tracking-wide uppercase">
        💰 {tr("proformaNew.financialSummaryTitle")}
      </h3>
      <p className="text-xs text-neutral-400 -mt-1">{tr("proformaNew.financialSummaryDesc")}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
        <PanoramaRow
          label={tr("proformaNew.grossQuoteLabel")}
          value={totalOrcamento}
          subtitle={`${itemsArray.length} ${tr("proformaNew.dossiersWordSuffix")}`}
        />
        <PanoramaRow
          label={tr("proformaNew.clientRetentionLabel")}
          value={-totalClientAmount}
          subtitle={tr("proformaNew.clientRetentionSubtitle")}
          negative
        />
        <PanoramaRow
          label={tr("proformaNew.totalInvoicedClientLabel")}
          value={totalFaturadoCliente}
          subtitle={tr("proformaNew.totalInvoicedClientSubtitle")}
          highlight
        />
        <PanoramaRow
          label={`${tr("proformaNew.toPayTo")} ${recipientShortLabel}`}
          value={-proformaGross}
          subtitle={tr("proformaNew.thisProforma")}
          negative
        />
        <div className="md:col-span-2 border-t border-lime/20 pt-2 mt-1">
          <PanoramaRow
            label={tr("proformaNew.eurostormKeepsLabel")}
            value={eurostormKeeps}
            subtitle={
              eurostormNegative
                ? tr("proformaNew.eurostormNegativeSubtitle")
                : tr("proformaNew.eurostormPositiveSubtitle")
            }
            highlight
            warning={eurostormNegative}
          />
        </div>
      </div>
    </div>
  );
}

function PanoramaRow({
  label,
  value,
  subtitle,
  highlight,
  negative,
  warning,
}: {
  label: string;
  value: number;
  subtitle?: string;
  highlight?: boolean;
  negative?: boolean;
  warning?: boolean;
}) {
  const formatted = `${value < 0 ? "−" : ""}${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value))} €`;
  const valueClass = warning
    ? "text-amber-300"
    : highlight
      ? "text-lime"
      : negative
        ? "text-neutral-400"
        : "text-neutral-200";
  return (
    <div className="flex items-baseline justify-between gap-3 rounded-md bg-neutral-950/40 px-3 py-2">
      <div className="min-w-0">
        <p
          className={`text-xs ${highlight ? "font-semibold text-neutral-200" : "text-neutral-400"} truncate`}
          title={label}
        >
          {label}
        </p>
        {subtitle && <p className="text-[10px] text-neutral-500 truncate">{subtitle}</p>}
      </div>
      <p className={`text-sm font-semibold tabular-nums font-mono whitespace-nowrap ${valueClass}`}>
        {formatted}
      </p>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <ErpShell>
      <main className="max-w-7xl mx-auto p-6 space-y-4">{children}</main>
    </ErpShell>
  );
}
